import React, { memo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  IconButton,
  Icon,
  Stack,
  Tooltip,
  Alert,
  Box,
  Badge,
  TableFooter,
} from "investira.react.components";
import { validators } from "investira.sdk";
import { Link } from "react-router-dom";
import { ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";

import { useConsultaContext } from "../../../../contexts/ConsultaContext";
import { PessoaImage, NumberTableCell } from "../../../../components/molecules";
import { displays } from "investira.react.lib";
import { PATHS } from "../../../../enums";
import { PrecisionNumberDisplay } from "../../../../components/molecules";

const DetalheTable = memo((props) => {
  const { detalhe } = props;

  function calcDecimal(pValue, pMinSigDigitos = 4) {
    if (pValue === undefined || pValue === null) {
      return pMinSigDigitos;
    }
    const xMatch = pValue.toString().match(/e-(\d+)/);
    const xExpoente = xMatch ? parseInt(xMatch[1], 10) : 0;

    // Calcula as casas decimais necessárias (expoente + dígitos significativos)
    const xDecimal = xExpoente + pMinSigDigitos;
    return xDecimal;
  }

  // Função para somar o total de algumas colunas usando reducer
  function somarTotaisColunas(pDetalhes, pColunas) {
    if (!Array.isArray(pDetalhes) || !Array.isArray(pColunas)) {
      console.error("Parâmetros inválidos para somarTotaisColunas");
      return {};
    }

    return pColunas.reduce((totais, coluna) => {
      totais[coluna] = pDetalhes.reduce((soma, item) => {
        // Suporta propriedades aninhadas usando notação ponto, ex: "var_contribuicao.financeiro_mensal"
        const valor = coluna
          .split(".")
          .reduce(
            (obj, key) => (obj && obj[key] !== undefined ? obj[key] : 0),
            item
          );
        return soma + (typeof valor === "number" ? valor : 0);
      }, 0);
      return totais;
    }, {});
  }

  function agruparPorAtivoTipoId(pDetalhe) {
    // Verifica se o input é um array
    if (!Array.isArray(pDetalhe)) {
      console.error("O parâmetro deve ser um array.");
      return {};
    }

    const agrupado = {};

    pDetalhe.forEach((item) => {
      const tipoId = item.ativo_tipo_id;

      // Se o tipo não existir no objeto, cria um array vazio para ele
      if (!agrupado[tipoId]) {
        agrupado[tipoId] = [];
      }

      // Adiciona o item ao grupo correspondente
      agrupado[tipoId].push(item);
    });

    // Ordena o objeto 'agrupado' por ordem alfabética das chaves
    const agrupadoOrdenado = {};
    Object.keys(agrupado)
      .sort((a, b) => {
        // Trata null ou undefined para evitar erros na ordenação
        if (a === null || a === undefined) return 1;
        if (b === null || b === undefined) return -1;
        // Converte para string para garantir ordenação correta
        return a.toString().localeCompare(b.toString(), "pt-BR");
      })
      .forEach((key) => {
        agrupadoOrdenado[key] = agrupado[key];
      });

    return agrupadoOrdenado;
  }

  const xVarTotals = somarTotaisColunas(detalhe, [
    "var.percentual_mensal",
    "var.financeiro_mensal",
    "var_contribuicao.percentual_mensal",
    "var_contribuicao.financeiro_mensal",
    "var_contribuicao_ewma.percentual_mensal",
    "var_contribuicao_ewma.financeiro_mensal",
  ]);

  return (
    <TableContainer
      id="table-container"
      sx={{
        overflow: "auto",
      }}
    >
      <Table size="small" sx={{ borderColor: "secondary" }}>
        <TableHead>
          <TableRow>
            <TableCell
              colSpan={11}
              sx={{ verticalAlign: "center", textAlign: "center" }}
            ></TableCell>
            <TableCell
              colSpan={6}
              sx={{
                textAlign: "center",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              VaR Paramétrico Mensal
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              colSpan={9}
              sx={{ verticalAlign: "bottom", textAlign: "center" }}
            ></TableCell>
            <TableCell
              colSpan={2}
              sx={{
                textAlign: "center",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              Volatilidade Anual %
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                lineHeight: 1,
                textAlign: "center",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              <Stack
                gap={1}
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <span>Isolado</span>
                <Tooltip
                  title={
                    <React.Fragment>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        <b>VaR Isolado</b>
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        Calcula o VaR de um ativo sem considerar correlações com
                        outros ativos.
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        sx={{ fontWeight: 500 }}
                      >
                        Valores de Referência:
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        Mínimo: Próximo de 0 para ativos de baixo risco (ex.:
                        títulos públicos).
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                      >
                        Ideal: Depende do ativo. Ex.: VaR diário de 1-3% para
                        ações individuais de grande capitalização.
                      </Typography>

                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        {`Máximo: Sem limite, mas VaR isolado elevado (ex.: >10%) reflete ativos de alto risco.`}
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                      >
                        Útil para avaliar o risco intrínseco de um ativo.
                        Comparar com o VaR total da carteira para entender
                        diversificação.
                      </Typography>
                    </React.Fragment>
                  }
                >
                  <span>
                    <Icon size={16} color={"primary"} iconName="information" />
                  </span>
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                lineHeight: 1,
                textAlign: "center",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              <Stack
                gap={1}
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <span>Contribuição (EULER)</span>
                <Tooltip
                  title={
                    <React.Fragment>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        <b>VaR Euler</b>
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        Mede a contribuição marginal de um ativo para o VaR
                        total da carteira, considerando correlações.
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        sx={{ fontWeight: 500 }}
                      >
                        Valores de Referência:
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        Mínimo: Zero, se o ativo não contribui para o risco da
                        carteira (raro).
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                      >
                        Ideal: Proporcional à participação do ativo na carteira.
                        Ex.: um ativo com 10% da carteira deve contribuir com
                        ~10% do VaR total.
                      </Typography>

                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        {`Limitado pelo VaR total da carteira.`}
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                      >
                        Ajuda a identificar quais ativos mais contribuem para o
                        risco da carteira. A soma dos VaR Euler deve igualar o
                        VaR total.
                      </Typography>
                    </React.Fragment>
                  }
                >
                  <span>
                    <Icon size={16} color={"primary"} iconName="information" />
                  </span>
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                verticalAlign: "bottom",
                textAlign: "center",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              Contribuição (EULER) EWMA
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ verticalAlign: "bottom" }}></TableCell>
            <TableCell sx={{ verticalAlign: "bottom" }}>Ativo</TableCell>
            <TableCell sx={{ verticalAlign: "bottom" }}>ISIN</TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Financeiro
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              Participação %
            </TableCell>

            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
              }}
            >
              Sharpe
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              Sharpe EWMA
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Beta
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Treynor
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                width: "117px",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              Vol. Ano
            </TableCell>

            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              Vol. EWMA
            </TableCell>

            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
              }}
            >
              Var (%)
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              Financeiro
            </TableCell>

            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
              }}
            >
              Var (%)
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              Financeiro
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                whiteSpace: "nowrap",
              }}
            >
              Var (%)
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              Financeiro
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {Object.entries(agruparPorAtivoTipoId(detalhe)).map(
            ([xAtivoTipoId, xDetalhes]) => {
              return (
                <React.Fragment key={crypto.randomUUID()}>
                  {!validators.isNull(xAtivoTipoId) && (
                    <TableRow>
                      <TableCell colSpan="18" sx={{ pl: 4 }}>
                        <b>{ATIVO_TIPO[xAtivoTipoId]?.descricao}</b>
                      </TableCell>
                    </TableRow>
                  )}
                  {xDetalhes.map((xItem, xIndex) => {
                    const bgColor =
                      xIndex % 2 === 0
                        ? "secondary.darkness"
                        : "secondary.dark";

                    const xMercado = ATIVO_TIPO[xAtivoTipoId]?.mercado;
                    const xPath = xMercado
                      ? `/ativos${PATHS[xMercado.toLowerCase()].path}/${
                          xItem.ifeed_ativo_id || xItem.ativo_id
                        }`
                      : null;

                    return (
                      <React.Fragment key={crypto.randomUUID()}>
                        {!validators.isEmpty(xItem.erros) && (
                          <TableRow sx={{ backgroundColor: bgColor }}>
                            <TableCell colSpan={18} padding="none">
                              <Stack spacing={0.25}>
                                {xItem.erros.map((xErr, xErrIndex) => {
                                  return (
                                    <Alert
                                      key={`${xItem.isin}-erro-${xErrIndex}`}
                                      severity="warning"
                                      sx={{ py: 0.25, borderRadius: 0 }}
                                      variant="outlined"
                                    >
                                      {xErr.descricao}
                                    </Alert>
                                  );
                                })}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ backgroundColor: bgColor }}>
                          <TableCell sx={{ pl: 6, width: "24px" }}>
                            {Boolean(xItem?.emissor?.logo_pessoa_id) && (
                              <Link
                                to={xPath}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PessoaImage
                                  logoPessoaId={xItem.emissor.logo_pessoa_id}
                                  alt={xItem?.emissor.pessoa_fantasia}
                                  hasLogo={Boolean(
                                    xItem?.emissor.logo_pessoa_id
                                  )}
                                  size={24}
                                />
                              </Link>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Badge
                              badgeContent={xItem.marcacao}
                              color="warning"
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              <Box pr={1.5}>{xItem.ativo}</Box>
                            </Badge>
                          </TableCell>
                          <TableCell>{xItem.isin || "---"}</TableCell>
                          {/* <TableCell>{xItem.ticker || "---"}</TableCell> */}
                          <NumberTableCell
                            value={xItem.financeiro}
                            decimal={2}
                            separateThousand={true}
                          />
                          <TableCell>
                            <PrecisionNumberDisplay
                              value={xItem.participacao * 100}
                              threshold={0.0001}
                            />
                          </TableCell>

                          <NumberTableCell
                            value={xItem.sharpe}
                            decimal={calcDecimal(xItem.sharpe, 3)}
                            separateThousand={true}
                          />
                          <NumberTableCell
                            value={xItem.sharpe_ewma}
                            decimal={calcDecimal(xItem.sharpe_ewma, 3)}
                            separateThousand={true}
                          />
                          <NumberTableCell
                            value={xItem.beta}
                            decimal={calcDecimal(xItem.beta, 3)}
                            separateThousand={true}
                          />
                          <NumberTableCell
                            value={xItem.treynor}
                            decimal={calcDecimal(xItem.treynor, 3)}
                            separateThousand={true}
                          />
                          <NumberTableCell
                            value={
                              xItem?.vol?.anual ? xItem.vol.anual * 100 : null
                            }
                            decimal={calcDecimal(xItem?.vol?.anual * 100, 4)}
                            separateThousand={true}
                            sx={{
                              borderLeft: "1px solid",
                              borderColor: "secondary.main",
                            }}
                          />
                          <NumberTableCell
                            value={
                              xItem?.vol?.anual_ewma
                                ? xItem.vol.anual_ewma * 100
                                : null
                            }
                            decimal={calcDecimal(
                              xItem?.vol?.anual_ewma * 100,
                              4
                            )}
                            separateThousand={true}
                          />
                          <NumberTableCell
                            value={
                              xItem?.var?.percentual_mensal
                                ? xItem.var.percentual_mensal * 100
                                : null
                            }
                            decimal={calcDecimal(
                              xItem?.var?.percentual_mensal * 100,
                              4
                            )}
                            separateThousand={true}
                            sx={{
                              borderLeft: "1px solid",
                              borderColor: "secondary.main",
                            }}
                          />

                          <NumberTableCell
                            value={
                              xItem?.var?.financeiro_mensal
                                ? xItem.var.financeiro_mensal
                                : null
                            }
                            decimal={2}
                            separateThousand={true}
                          />

                          <NumberTableCell
                            value={
                              xItem?.var_contribuicao?.percentual_mensal
                                ? xItem.var_contribuicao.percentual_mensal * 100
                                : null
                            }
                            decimal={calcDecimal(
                              xItem?.var_contribuicao?.percentual_mensal * 100,
                              4
                            )}
                            separateThousand={true}
                            sx={{
                              borderLeft: "1px solid",
                              borderColor: "secondary.main",
                            }}
                          />

                          <NumberTableCell
                            value={
                              xItem?.var_contribuicao?.financeiro_mensal
                                ? xItem.var_contribuicao.financeiro_mensal
                                : null
                            }
                            decimal={2}
                            separateThousand={true}
                          />

                          <NumberTableCell
                            value={
                              xItem?.var_contribuicao_ewma?.percentual_mensal
                                ? xItem.var_contribuicao_ewma
                                    .percentual_mensal * 100
                                : null
                            }
                            decimal={calcDecimal(
                              xItem?.var_contribuicao_ewma?.percentual_mensal *
                                100,
                              4
                            )}
                            separateThousand={true}
                            sx={{
                              borderLeft: "1px solid",
                              borderColor: "secondary.main",
                            }}
                          />
                          <NumberTableCell
                            value={
                              xItem?.var_contribuicao_ewma?.financeiro_mensal
                                ? xItem.var_contribuicao_ewma.financeiro_mensal
                                : null
                            }
                            decimal={2}
                            separateThousand={true}
                          />
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            }
          )}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell
              colSpan={11}
              sx={{ verticalAlign: "bottom" }}
            ></TableCell>

            <NumberTableCell
              value={
                xVarTotals["var.percentual_mensal"]
                  ? xVarTotals["var.percentual_mensal"] * 100
                  : null
              }
              decimal={calcDecimal(
                xVarTotals["var.percentual_mensal"] * 100,
                4
              )}
              separateThousand={true}
              sx={{
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                fontWeight: "600",
              }}
            />

            <NumberTableCell
              value={
                xVarTotals["var.financeiro_mensal"]
                  ? xVarTotals["var.financeiro_mensal"]
                  : null
              }
              decimal={2}
              separateThousand={true}
              sx={{
                fontWeight: "600",
              }}
            />

            <NumberTableCell
              value={
                xVarTotals["var_contribuicao.percentual_mensal"]
                  ? xVarTotals["var_contribuicao.percentual_mensal"] * 100
                  : null
              }
              decimal={calcDecimal(
                xVarTotals["var_contribuicao.percentual_mensal"] * 100,
                4
              )}
              separateThousand={true}
              sx={{
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                fontWeight: "600",
              }}
            />

            <NumberTableCell
              value={
                xVarTotals["var_contribuicao.financeiro_mensal"]
                  ? xVarTotals["var_contribuicao.financeiro_mensal"]
                  : null
              }
              decimal={2}
              separateThousand={true}
              sx={{
                fontWeight: "600",
              }}
            />

            <NumberTableCell
              value={
                xVarTotals["var_contribuicao_ewma.percentual_mensal"]
                  ? xVarTotals["var_contribuicao_ewma.percentual_mensal"] * 100
                  : null
              }
              decimal={calcDecimal(
                xVarTotals["var_contribuicao_ewma.percentual_mensal"] * 100,
                4
              )}
              separateThousand={true}
              sx={{
                borderLeft: "1px solid",
                borderColor: "secondary.main",
                fontWeight: "600",
              }}
            />

            <NumberTableCell
              value={
                xVarTotals["var_contribuicao_ewma.financeiro_mensal"]
                  ? xVarTotals["var_contribuicao_ewma.financeiro_mensal"]
                  : null
              }
              decimal={2}
              separateThousand={true}
              sx={{
                fontWeight: "600",
              }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
});

export default DetalheTable;
